/**
 * The typed identity outcome of a ONE-SHOT RTK Query read.
 *
 * A read that crosses an identity change settles into one of two standardized rejections — a settle the
 * `identityScoped` fence converted, or the commit reconcile's own abort of a request it displaced (see
 * `./identity.scope`) — and neither is a failure: the flow that asked has to be abandoned, not reported. That
 * contract used to be enforced by a comment at every awaiting call site. Here it becomes a type: a caller
 * cannot reach `.data` without acknowledging the identity arm first, so handing the previous identity's — or an
 * empty — answer onward as if it were this identity's stops being writable by accident.
 *
 * Real failures are deliberately NOT folded into the union. They stay on the throw channel, so a caller keeps
 * whatever error handling it already had (this is a hazard fix, not call-site shrinkage).
 *
 * RTK-free by structural typing, like every other helper here, so it stays exportable from the sideEffects-free
 * `src/index.ts` without dragging RTKQ/immer onto the eager boot path.
 */

import { type IdentityChangedAbort, type IdentityChangedError, isIdentityChangedError } from './identity.scope';

/**
 * Minimal structural shape of an RTK Query `initiate()` result — declared here on purpose, NOT imported from
 * `@reduxjs/toolkit`, so this stays RTK-free and the eager `appstore` bundle never pulls RTKQ/immer. The real
 * RTKQ result types are structurally compatible (they have these members plus more).
 *
 * Lives here rather than in `sagautils/run.query.ts` (where it started) because both read helpers need it.
 */
export interface QueryInitiateResult<R> {
    unwrap(): Promise<R>;
    unsubscribe(): void;
}

/**
 * What a one-shot read settled as: this identity's data, or an identity-crossing rejection.
 *
 * `identityRejected: true` means **discard this read and abort the flow** — the answer belongs to an identity
 * that is no longer current.
 *
 * What the ENTRY does afterwards is worth stating exactly rather than as "it refetches", because a plain
 * (unforced) re-`initiate` has THREE outcomes, and only one of them runs a request:
 * - a request IN FLIGHT on the key — RTKQ's condition dedupes the re-`initiate` ONTO it, and the read settles on
 *   that request's outcome;
 * - a COLD rejection (nothing ever fulfilled this entry, so it has no `fulfilledTimeStamp`) — a FRESH request
 *   runs, under the CURRENT token identity, which mid-window can "succeed" with an answer the flow no longer
 *   means to ask for (after a logout, the anonymous gate's empty list);
 * - a WARM rejection (an earlier fulfill's `fulfilledTimeStamp` survives it, as does its `data`) — the condition
 *   SKIPS, and the promise re-surfaces the entry's CURRENT rejected state, so the same rejection keeps coming
 *   back until a forced refetch, an invalidation, or eviction moves it.
 *
 * Proactive repair is per policy class and is not promised generically: the commit reconcile repopulates purge-
 * and revalidate-class entries, and the standing rejection repair covers those two classes as well. A fenced
 * endpoint declared `invariant` gets neither — expressible, unused, and deliberately unrepaired — so a warm
 * rejection there can rest under an active subscription until eviction.
 *
 * The operative advice is the same for all three: abort, and retry only after the commit signal — never
 * immediately. See the consumer contract in `docs/agentic-ai-rtk-query-guide.md`.
 *
 * The discriminant names what THIS READ'S SETTLE was, and is deliberately not `identityChanged`: it is not a
 * time-of-use identity guarantee, and two documented windows have `identityRejected: false` coexisting with an
 * identity that already moved. A warm-cache `initiate()` resolves captured cache state without invoking the
 * fence at all, so a read between the token tick and the commit serves the previous identity's data; and the
 * fence's own final-check → `fulfilled`-dispatch microtask residual (see `identityScoped`) is not closed by it
 * either. The commit reconcile heals both. A caller that needs time-of-use freshness rather than a settle
 * classification wants `currentIdentityGeneration` / `isCurrentGeneration`.
 */
export type QueryReadResult<R> =
    | { identityRejected: false; data: R }
    | { identityRejected: true; error: IdentityChangedError | IdentityChangedAbort };

/**
 * Classify ONE settle of an already-dispatched read; everything that is not an identity crossing rethrows.
 *
 * Owns no subscription — the CALLER releases, which is what lets the saga bridge keep releasing from a
 * generator `finally` (at cancellation) while `readQuery` releases at settle. Lib-internal: shared with
 * `sagautils/run.query.ts` and deliberately absent from the public barrel, so the only exported entry points
 * are the two that also own the release.
 */
export async function classifyRead<R>(subscription: QueryInitiateResult<R>): Promise<QueryReadResult<R>> {
    try {
        return { identityRejected: false, data: await subscription.unwrap() };
    } catch (err) {
        if (isIdentityChangedError(err)) {
            return { identityRejected: true, error: err };
        }
        throw err;
    }
}

/**
 * One-shot read of a FRESH `initiate()` result for promise contexts (a listener effect, an async accessor):
 * await it, classify the settle, and release the subscription — the same lifecycle the hand-rolled
 * `dispatch(...initiate())` / `unwrap()` / `unsubscribe()` accessors it replaces already had.
 *
 * Pass a subscription this call OWNS: the release happens when the promise settles, so a shared or reused
 * subscription would be released out from under its other holder. In a saga use `runQuery` instead — it
 * releases from a generator `finally`, so a cancelled saga does not wait for a hung request to settle.
 *
 * @example
 * const read = await readQuery(dispatch(featureApi.endpoints.getItems.initiate()));
 * if (read.identityRejected) {
 *     return; // not a failure — the identity moved; abort silently
 * }
 * use(read.data);
 */
export async function readQuery<R>(subscription: QueryInitiateResult<R>): Promise<QueryReadResult<R>> {
    try {
        return await classifyRead(subscription);
    } finally {
        subscription.unsubscribe();
    }
}
